import React, { useState } from 'react';
import { useLoaderData, Link } from 'react-router-dom';
import TopAppBar from '../components/TopAppBar';
import BottomNavBar from '../components/BottomNavBar';
import Toast from '../components/Toast';
import { Credential, CredentialStatus } from '../types/contractor';
import {
  getMyCredentials,
  uploadCredentialDocument,
  updateRegistrationNumber,
  submitCredentialsForReview,
  downloadCredentialDocument,
} from '../api/credential';

export async function credentialsLoader() {
  try {
    const credentials = await getMyCredentials();
    return { credentials };
  } catch (error: any) {
    console.error('credentialsLoader failed:', error?.response?.status, error?.response?.data ?? error?.message);
    return { credentials: [], loadError: `Failed to load credentials (${error?.response?.status ?? 'network error'})` };
  }
}

interface CredentialMeta {
  type: string;
  label: string;
  requiresDoc: boolean;
  requiresReg: boolean;
  icon: string;
}

const CREDENTIAL_META: CredentialMeta[] = [
  { type: 'CompanyRegistration', label: 'Company Registration', requiresDoc: false, requiresReg: true,  icon: 'badge' },
  { type: 'NHBRC',               label: 'NHBRC',                requiresDoc: false, requiresReg: false, icon: 'home_work' },
  { type: 'Insurance',           label: 'Insurance',            requiresDoc: false, requiresReg: false, icon: 'policy' },
  { type: 'BBBEE',               label: 'B-BBEE',               requiresDoc: false, requiresReg: false, icon: 'diversity_3' },
];

function StatusBadge({ status }: { status: CredentialStatus }) {
  const config = {
    NotSubmitted: { label: 'Not submitted', classes: 'bg-surface-container text-outline' },
    UnderReview:  { label: 'Under review',  classes: 'bg-amber-100 text-amber-700' },
    Confirmed:    { label: 'Confirmed',     classes: 'bg-green-100 text-green-700' },
    Rejected:     { label: 'Rejected',      classes: 'bg-red-100 text-red-700' },
  }[status];

  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${config.classes}`}>
      {status === 'Confirmed' && <span className="material-symbols-outlined text-[12px]" style={{ fontVariationSettings: "'FILL' 1" }}>check_circle</span>}
      {config.label}
    </span>
  );
}

const CredentialsPage: React.FC = () => {
  const { credentials: initialCredentials, loadError } = useLoaderData() as { credentials: Credential[]; loadError?: string };
  const [credentials, setCredentials] = useState<Credential[]>(initialCredentials);
  const [uploading, setUploading] = useState<string | null>(null);
  const [savingReg, setSavingReg] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const getCredential = (type: string): Credential | undefined =>
    credentials.find(c => c.type === type);

  const refresh = async () => {
    const updated = await getMyCredentials();
    setCredentials(updated);
  };

  const handleFileUpload = async (credentialId: number, file: File) => {
    setUploading(`doc-${credentialId}`);
    try {
      await uploadCredentialDocument(credentialId, file);
      await refresh();
      setToast({ message: 'Document uploaded.', type: 'success' });
    } catch {
      setToast({ message: 'Failed to upload document.', type: 'error' });
    } finally {
      setUploading(null);
    }
  };

  const handleRegBlur = async (credentialId: number, value: string) => {
    setSavingReg(credentialId);
    try {
      await updateRegistrationNumber(credentialId, value);
    } catch {
      setToast({ message: 'Failed to save registration number.', type: 'error' });
    } finally {
      setSavingReg(null);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    setValidationErrors([]);
    try {
      const result = await submitCredentialsForReview();
      if (result.success) {
        await refresh();
        setToast({ message: result.message ?? 'Credentials submitted for review.', type: 'success' });
      } else {
        setSubmitError(result.message ?? 'Submission failed.');
        setValidationErrors(result.validationErrors ?? []);
      }
    } catch (err: any) {
      const data = err?.response?.data;
      setSubmitError(data?.message ?? 'Submission failed.');
      setValidationErrors(data?.validationErrors ?? []);
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = credentials.some(c => c.status === 'NotSubmitted' || c.status === 'Rejected');
  const isEditable = (status: CredentialStatus) => status === 'NotSubmitted' || status === 'Rejected';

  return (
    <div className="min-h-screen bg-surface-container-lowest pb-24 md:pb-8">
      <TopAppBar />

      <main className="max-w-2xl mx-auto px-4 py-8 space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <Link
            to="/contractor-profile"
            className="flex items-center gap-1 text-sm text-outline hover:text-on-surface transition-colors"
          >
            <span className="material-symbols-outlined text-[18px]">arrow_back</span>
            Back to profile
          </Link>
        </div>

        <h1 className="text-2xl font-bold text-on-surface">Compliance &amp; Credentials</h1>
        <p className="text-sm text-outline">
          Upload your compliance documents and submit them for review. Confirmed credentials appear as badges on your public profile.
        </p>

        {loadError && (
          <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
            <span className="material-symbols-outlined text-[18px] flex-shrink-0 mt-0.5">error</span>
            <span>{loadError}</span>
          </div>
        )}

        {CREDENTIAL_META.map(meta => {
          const cred = getCredential(meta.type);
          if (!cred) return null;

          const editable = isEditable(cred.status);
          const isUploadingThis = uploading === `doc-${cred.id}`;

          return (
            <div key={meta.type} className="bg-surface rounded-2xl shadow-sm p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={`material-symbols-outlined text-2xl ${cred.status === 'Confirmed' ? 'text-green-600' : 'text-outline'}`}>
                    {meta.icon}
                  </span>
                  <span className="font-semibold text-on-surface">{meta.label}</span>
                </div>
                <StatusBadge status={cred.status} />
              </div>

              {/* Rejection reason callout */}
              {cred.status === 'Rejected' && cred.rejectionReason && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                  <span className="material-symbols-outlined text-[18px] flex-shrink-0 mt-0.5">warning</span>
                  <span>{cred.rejectionReason}</span>
                </div>
              )}

              {/* Registration number */}
              {meta.requiresReg && (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <label className="text-xs font-medium text-outline">Registration number</label>
                    {meta.type === 'CompanyRegistration' && (
                      <span className="text-xs font-semibold text-error bg-error/10 px-1.5 py-0.5 rounded">Required</span>
                    )}
                  </div>
                  <input
                    type="text"
                    defaultValue={cred.registrationNumber ?? ''}
                    disabled={!editable || savingReg === cred.id}
                    onBlur={e => editable && handleRegBlur(cred.id, e.target.value)}
                    placeholder="Enter registration number"
                    className="w-full px-3 py-2 text-sm border border-outline-variant rounded-lg bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  {savingReg === cred.id && (
                    <p className="text-xs text-outline mt-1">Saving…</p>
                  )}
                </div>
              )}

              {/* Document upload */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <label className="text-xs font-medium text-outline">
                    Supporting document
                  </label>
                  {meta.requiresDoc && (
                    <span className="text-xs font-semibold text-error bg-error/10 px-1.5 py-0.5 rounded">Required</span>
                  )}
                </div>

                {cred.hasDocument ? (
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => downloadCredentialDocument(cred.id)}
                      className="flex items-center gap-1 text-sm text-primary hover:underline"
                    >
                      <span className="material-symbols-outlined text-[16px]">open_in_new</span>
                      View document
                    </button>
                    {editable && (
                      <label className="flex items-center gap-1 text-xs text-outline cursor-pointer hover:text-on-surface">
                        <span className="material-symbols-outlined text-[16px]">upload</span>
                        Replace
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          className="hidden"
                          disabled={isUploadingThis}
                          onChange={e => {
                            const file = e.target.files?.[0];
                            if (file) handleFileUpload(cred.id, file);
                            e.target.value = '';
                          }}
                        />
                      </label>
                    )}
                  </div>
                ) : editable ? (
                  <label className={`flex items-center justify-center gap-2 w-full py-3 border-2 border-dashed rounded-lg text-sm cursor-pointer transition-colors ${isUploadingThis ? 'border-primary/30 text-primary/50' : 'border-outline-variant text-outline hover:border-primary hover:text-primary'}`}>
                    <span className="material-symbols-outlined text-[18px]">
                      {isUploadingThis ? 'hourglass_empty' : 'upload_file'}
                    </span>
                    {isUploadingThis ? 'Uploading…' : 'Upload document (PDF, JPG, PNG)'}
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="hidden"
                      disabled={isUploadingThis}
                      onChange={e => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(cred.id, file);
                        e.target.value = '';
                      }}
                    />
                  </label>
                ) : (
                  <p className="text-sm text-outline-variant italic">No document uploaded.</p>
                )}
              </div>
            </div>
          );
        })}

        {/* Submit section */}
        <div className="bg-surface rounded-2xl shadow-sm p-6 space-y-3">
          {submitError && (
            <div className="text-sm text-error bg-error/10 rounded-lg p-3">{submitError}</div>
          )}
          {validationErrors.length > 0 && (
            <ul className="text-sm text-error space-y-1">
              {validationErrors.map((e, i) => (
                <li key={i} className="flex items-start gap-1">
                  <span className="material-symbols-outlined text-[14px] mt-0.5 flex-shrink-0">error</span>
                  {e}
                </li>
              ))}
            </ul>
          )}
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            className="w-full py-3 rounded-xl font-semibold text-sm bg-primary text-on-primary hover:bg-primary/90 transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? 'Submitting…' : 'Submit for review'}
          </button>
          <p className="text-xs text-outline text-center">
            All credentials marked as "Not submitted" or "Rejected" will be submitted together.
          </p>
        </div>
      </main>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onDismiss={() => setToast(null)}
        />
      )}

      <BottomNavBar />
    </div>
  );
};

export default CredentialsPage;
